/** Partner console — Reseller Settings page.
 *
 * Lets a reseller manage their own profile: name, contact details, address,
 * tax/VAT ID, logo upload, and invoice prefix (moved here from the overview).
 * Reseller-only; commercials are redirected to /partner. */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { CountrySelector } from "@/components/CountrySelector";
import { PartnerPageHeader } from "@/components/PartnerLayout";
import PhoneInput from "@/components/PhoneInput";
import { Spinner } from "@/components/Spinner";
import {
  usePartnerMe,
  useUpdatePartnerSettings,
  useUploadPartnerLogo,
} from "@/lib/partner";

function SettingsSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card space-y-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function PartnerSettingsPage() {
  const { t } = useTranslation();
  const me = usePartnerMe();
  const update = useUpdatePartnerSettings();
  const uploadLogo = useUploadPartnerLogo();
  const [seeded, setSeeded] = useState(false);

  // Profile identity fields
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [taxId, setTaxId] = useState("");

  // Address fields (Stripe address shape)
  const [addrLine1, setAddrLine1] = useState("");
  const [addrLine2, setAddrLine2] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [addrPostal, setAddrPostal] = useState("");
  const [addrCountry, setAddrCountry] = useState("");

  // Invoice prefix
  const [prefix, setPrefix] = useState("");
  const normalizedPrefix = prefix
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 10);

  // Seed form from remote data once
  useEffect(() => {
    if (seeded || !me.data) return;
    const p = me.data;
    setName(p.name ?? "");
    setContactEmail(p.contact_email ?? "");
    setContactPhone(p.contact_phone ?? "");
    setTaxId(p.tax_id ?? "");
    setPrefix(p.invoice_prefix ?? "");
    const addr = p.address ?? {};
    setAddrLine1(addr.line1 ?? "");
    setAddrLine2(addr.line2 ?? "");
    setAddrCity(addr.city ?? "");
    setAddrPostal(addr.postal_code ?? "");
    setAddrCountry(addr.country ?? "");
    setSeeded(true);
  }, [me.data, seeded]);

  if (me.isLoading) return <Spinner fullscreen label={t("common.loading")} />;
  if (!me.data) return null;
  if (me.data.kind !== "reseller") return <Navigate to="/partner" replace />;

  const p = me.data;
  const currentPrefix =
    p.invoice_prefix ??
    (p.name ?? "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 10);

  const saveProfile = () => {
    update.mutate({
      name: name.trim() || undefined,
      contact_email: contactEmail.trim() || undefined,
      contact_phone: contactPhone.trim() || undefined,
      tax_id: taxId.trim() || undefined,
      address: {
        line1: addrLine1,
        line2: addrLine2,
        city: addrCity,
        postal_code: addrPostal,
        country: addrCountry,
      },
    });
  };

  const savePrefix = () => {
    update.mutate({ invoice_prefix: normalizedPrefix });
  };

  return (
    <>
      <PartnerPageHeader
        eyebrow={t("partner.settings.eyebrow", "Settings")}
        title={t("partner.settings.title", "Reseller settings")}
        description={t(
          "partner.settings.lead",
          "Manage your reseller profile, contact information, address, and billing identity.",
        )}
      />

      {/* Logo */}
      <SettingsSection title={t("partner.settings.logoTitle", "Logo")}>
        <div className="flex flex-wrap items-center gap-4">
          {p.logo_url ? (
            <img
              src={p.logo_url}
              alt=""
              className="h-16 w-16 rounded border border-nb-border object-contain bg-white/5"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded border border-dashed border-nb-border text-nb-text-3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5" />
                <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M3 15l5-5 4 4 3-3 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          <div className="space-y-1.5">
            <label className="btn-ghost cursor-pointer text-sm">
              {uploadLogo.isPending
                ? t("common.loading")
                : t("partner.settings.uploadLogo", "Upload logo")}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadLogo.mutate(f);
                  e.target.value = "";
                }}
              />
            </label>
            <p className="text-xs text-nb-text-3">
              {t("partner.settings.logoHint", "PNG or SVG recommended. Max 3 MB.")}
            </p>
            {uploadLogo.isError && (
              <p className="text-xs text-cvp-danger">
                {t("partner.settings.logoError", "Upload failed.")}
              </p>
            )}
          </div>
        </div>
      </SettingsSection>

      {/* Identity & Contact */}
      <SettingsSection
        title={t("partner.settings.identityTitle", "Identity & contact")}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label">
              {t("partner.settings.fieldName", "Reseller / company name")}
            </label>
            <input
              className="input"
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="label">
              {t("partner.settings.fieldEmail", "Contact e-mail")}
            </label>
            <input
              className="input"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="label">
              {t("partner.settings.fieldPhone", "Contact phone")}
            </label>
            <PhoneInput value={contactPhone} onChange={setContactPhone} />
          </div>
          <div>
            <label className="label">
              {t("partner.settings.fieldTaxId", "VAT / tax ID")}
            </label>
            <input
              className="input font-mono"
              value={taxId}
              placeholder="FR12345678901"
              onChange={(e) => setTaxId(e.target.value)}
            />
          </div>
        </div>

        <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-3">
          {t("partner.settings.addressTitle", "Billing address")}
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label">
              {t("partner.settings.addrLine1", "Address line 1")}
            </label>
            <input
              className="input"
              value={addrLine1}
              onChange={(e) => setAddrLine1(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label">
              {t("partner.settings.addrLine2", "Address line 2")}
            </label>
            <input
              className="input"
              value={addrLine2}
              onChange={(e) => setAddrLine2(e.target.value)}
            />
          </div>
          <div>
            <label className="label">
              {t("partner.settings.addrCity", "City")}
            </label>
            <input
              className="input"
              value={addrCity}
              onChange={(e) => setAddrCity(e.target.value)}
            />
          </div>
          <div>
            <label className="label">
              {t("partner.settings.addrPostal", "Postal code")}
            </label>
            <input
              className="input"
              value={addrPostal}
              onChange={(e) => setAddrPostal(e.target.value)}
            />
          </div>
          <div>
            <label className="label">
              {t("partner.settings.addrCountry", "Country")}
            </label>
            <CountrySelector
              value={addrCountry}
              onChange={(code) => setAddrCountry(code)}
              allowEmpty
            />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={update.isPending}
            onClick={saveProfile}
          >
            {update.isPending ? t("common.saving") : t("common.save")}
          </button>
          {update.isSuccess && (
            <span className="text-xs text-nb-mint">{t("common.saved")}</span>
          )}
          {update.isError && (
            <span className="text-xs text-cvp-danger">
              {t("common.saveError", "Save failed.")}
            </span>
          )}
        </div>
      </SettingsSection>

      {/* Invoice prefix */}
      <SettingsSection
        title={t("partner.overview.invoicePrefixTitle", "Invoice prefix")}
      >
        <p className="text-xs text-nb-text-3">
          {t("partner.overview.invoicePrefixHelp")}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input w-40 font-mono uppercase"
            maxLength={10}
            placeholder={t("partner.overview.invoicePrefixDerived")}
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
          />
          <span className="text-xs text-nb-text-3">
            {t("partner.overview.invoicePrefixPreview", {
              example: `${normalizedPrefix || currentPrefix}-${new Date().getFullYear()}-00001`,
            })}
          </span>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={update.isPending || normalizedPrefix === currentPrefix}
            onClick={savePrefix}
          >
            {update.isPending ? t("common.saving") : t("common.save")}
          </button>
          {update.isSuccess && (
            <span className="text-xs text-nb-mint">{t("common.saved")}</span>
          )}
        </div>
      </SettingsSection>

      {/* Read-only: referral code */}
      <SettingsSection
        title={t("partner.settings.referralTitle", "Referral code")}
      >
        <p className="text-xs text-nb-text-3">
          {t(
            "partner.settings.referralHint",
            "Share this code so new customers are attributed to you.",
          )}
        </p>
        <div className="flex items-center gap-3">
          <span className="rounded border border-nb-border bg-slate-900/40 px-3 py-1.5 font-mono text-sm text-nb-text">
            {p.referral_code}
          </span>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() =>
              navigator.clipboard.writeText(p.referral_code).catch(() => undefined)
            }
          >
            {t("common.copy", "Copy")}
          </button>
        </div>
      </SettingsSection>
    </>
  );
}
